import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getAdminToken = () =>
  sessionStorage.getItem("access_token") ||
  sessionStorage.getItem("token") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("admin_token") ||
  "";

const LEVEL_STYLES = {
  high: { bg: "bg-red-950/40 border-red-700/60", badge: "bg-red-600 text-white", label: "🔴 ALTO" },
  medium: { bg: "bg-amber-950/40 border-amber-700/60", badge: "bg-amber-500 text-black", label: "🟡 MEDIO" },
  low: { bg: "bg-gray-700/40 border-gray-600/60", badge: "bg-gray-500 text-white", label: "🟢 BASSO" },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export const SuspiciousOrdersWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState("all"); // all | high | medium | low

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getAdminToken();
      const r = await axios.get(`${API}/admin/orders/suspicious`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data || {});
    } catch (e) {
      console.error("[suspicious-orders] fetch failed", e?.response?.status);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const items = (data?.items || []).filter((i) => filter === "all" || i.level === filter);
  const visibleItems = expanded ? items : items.slice(0, 5);

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="suspicious-orders-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          Ordini Sospetti
          {data && data.total > 0 && (
            <span className="ml-2 bg-amber-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
              {data.total}
            </span>
          )}
        </CardTitle>
        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="text-gray-300 hover:text-white p-1"
          data-testid="suspicious-refresh"
          aria-label="Aggiorna"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </CardHeader>
      <CardContent>
        {/* Counters */}
        {data && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <FilterChip
              active={filter === "high"} all={filter === "all"}
              onClick={() => setFilter(filter === "high" ? "all" : "high")}
              label="🔴 Alto" count={data.by_level?.high || 0} color="text-red-400"
              testid="susp-filter-high"
            />
            <FilterChip
              active={filter === "medium"} all={filter === "all"}
              onClick={() => setFilter(filter === "medium" ? "all" : "medium")}
              label="🟡 Medio" count={data.by_level?.medium || 0} color="text-amber-400"
              testid="susp-filter-medium"
            />
            <FilterChip
              active={filter === "low"} all={filter === "all"}
              onClick={() => setFilter(filter === "low" ? "all" : "low")}
              label="🟢 Basso" count={data.by_level?.low || 0} color="text-gray-300"
              testid="susp-filter-low"
            />
          </div>
        )}

        {data && data.total === 0 && (
          <p className="text-sm text-emerald-400 text-center py-6">
            ✅ Nessun ordine sospetto rilevato. Tutto in ordine!
          </p>
        )}

        {items.length > 0 && (
          <div className="space-y-2">
            {visibleItems.map((it) => {
              const sty = LEVEL_STYLES[it.level] || LEVEL_STYLES.low;
              return (
                <div
                  key={it.order_id}
                  className={`rounded-lg border p-3 ${sty.bg}`}
                  data-testid={`susp-item-${it.order_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${sty.badge}`}>
                        {sty.label}
                      </span>
                      <span className="text-xs font-mono text-gray-400">
                        #{(it.order_id || "").slice(0, 8)}
                      </span>
                      <span className="text-xs uppercase font-semibold text-gray-300">
                        {it.payment_method}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-white">€ {Number(it.amount || 0).toFixed(2)}</span>
                  </div>
                  <p className="text-sm font-semibold text-white">{it.reason}</p>
                  <p className="text-xs text-gray-300 mt-1">{it.hint}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-400">
                    <span>👤 {it.customer_name || "—"}</span>
                    <span>📧 {it.customer_email || "—"}</span>
                    <span>📅 {formatDate(it.order_date)}</span>
                    <span>stato: <span className="text-gray-200">{it.status}</span> · pag: <span className="text-gray-200">{it.payment_status || "—"}</span></span>
                  </div>
                  <div className="mt-2">
                    <a
                      href={`/gestione/ordini/${it.order_id}`}
                      className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"
                      data-testid={`susp-open-${it.order_id}`}
                    >
                      Apri ordine <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              );
            })}

            {items.length > 5 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full text-sm text-amber-300 hover:text-amber-200 flex items-center justify-center gap-1 py-2"
                data-testid="susp-expand-btn"
              >
                {expanded
                  ? <>Mostra meno <ChevronUp className="w-4 h-4" /></>
                  : <>Mostra altri {items.length - 5} ordini <ChevronDown className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        )}

        <p className="text-[11px] text-gray-500 mt-4">
          Controlli automatici su Satispay, Stripe, Klarna, Card, PayPal, Bonifico e Contrassegno.
          Aggiornato ogni volta che apri la dashboard.
        </p>
      </CardContent>
    </Card>
  );
};

const FilterChip = ({ active, all, onClick, label, count, color, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`rounded-lg p-2 text-center transition-colors border ${
      active
        ? "bg-amber-500/20 border-amber-500"
        : "bg-gray-900/60 border-gray-700 hover:border-gray-500"
    } ${!active && !all ? "opacity-50" : ""}`}
  >
    <p className="text-[10px] text-gray-400 uppercase">{label}</p>
    <p className={`text-2xl font-bold mt-0.5 ${color}`}>{count}</p>
  </button>
);

export default SuspiciousOrdersWidget;
